const normalizeModelKeyV820 = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const createSavedRowIdV820 = (index) => `mapping-saved-${index}`;

const normalizeModelIdPrefixV820 = (value = '') =>
  String(value || '')
    .trim()
    .replace(/^-+|-+$/g, '');

export const applyModelIdPrefixV820 = (modelName, modelIdPrefix = '') => {
  const normalizedModelName = String(modelName || '').trim();
  const prefix = normalizeModelIdPrefixV820(modelIdPrefix);
  if (!normalizedModelName || !prefix) return normalizedModelName;
  return `${prefix}-${normalizedModelName}`;
};

const providerModelNameV820 = (modelName, channelModels, modelIdPrefix) => {
  const trimmedModelName = String(modelName || '').trim();
  const prefix = normalizeModelIdPrefixV820(modelIdPrefix);
  if (!trimmedModelName || !prefix) return trimmedModelName;

  const declaredModel = (
    Array.isArray(channelModels) ? channelModels : []
  ).find(
    (candidate) =>
      normalizeModelKeyV820(candidate) ===
      normalizeModelKeyV820(trimmedModelName)
  );
  const normalizedDeclaredModel = String(declaredModel || '').trim();
  const prefixWithSeparator = `${prefix}-`;
  return normalizedDeclaredModel.startsWith(prefixWithSeparator)
    ? normalizedDeclaredModel.slice(prefixWithSeparator.length)
    : trimmedModelName;
};

export const getUpstreamModelSuggestionsV820 = (
  channelModels,
  modelIdPrefix = ''
) =>
  Array.from(
    new Set(
      (Array.isArray(channelModels) ? channelModels : [])
        .map((modelName) =>
          providerModelNameV820(modelName, channelModels, modelIdPrefix)
        )
        .filter(Boolean)
    )
  ).sort((left, right) => left.localeCompare(right));

export const parseModelMappingRowsV820 = (
  modelMapping,
  targetOnlyModels = [],
  channelModels = [],
  modelIdPrefix = ''
) => {
  if (typeof modelMapping !== 'string' || modelMapping.trim() === '') {
    return [];
  }

  let parsed;
  try {
    parsed = JSON.parse(modelMapping);
  } catch {
    return [];
  }
  if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
    return [];
  }

  const targetOnlyKeys = new Set(
    (Array.isArray(targetOnlyModels) ? targetOnlyModels : [])
      .map((modelName) =>
        providerModelNameV820(modelName, channelModels, modelIdPrefix)
      )
      .map(normalizeModelKeyV820)
      .filter(Boolean)
  );

  return Object.entries(parsed)
    .filter(
      ([publicModel, upstreamModel]) =>
        typeof publicModel === 'string' && typeof upstreamModel === 'string'
    )
    .map(([publicModel, upstreamModel], index) => {
      const providerModel = providerModelNameV820(
        upstreamModel,
        channelModels,
        modelIdPrefix
      );
      return {
        id: createSavedRowIdV820(index),
        upstreamModel: providerModel,
        publicModel,
        targetOnly: targetOnlyKeys.has(normalizeModelKeyV820(providerModel)),
      };
    });
};

export const buildModelMappingSubmissionV820 = (
  rows,
  channelModels,
  modelIdPrefix = ''
) => {
  const normalizedRows = (Array.isArray(rows) ? rows : []).map((row) => ({
    upstreamModel: String(row?.upstreamModel || '').trim(),
    publicModel: String(row?.publicModel || '').trim(),
    targetOnly: Boolean(row?.targetOnly),
  }));
  const activeRows = normalizedRows.filter(
    (row) => row.upstreamModel || row.publicModel
  );
  if (activeRows.some((row) => !row.upstreamModel || !row.publicModel)) {
    return { error: 'incomplete' };
  }

  const publicKeys = new Set();
  for (const row of activeRows) {
    const publicKey = normalizeModelKeyV820(row.publicModel);
    const upstreamKey = normalizeModelKeyV820(row.upstreamModel);
    if (publicKey === upstreamKey) {
      return { error: 'same_name' };
    }
    if (publicKeys.has(publicKey)) {
      return { error: 'duplicate_public_name' };
    }
    publicKeys.add(publicKey);
  }

  const declaredModels = new Set(
    getUpstreamModelSuggestionsV820(channelModels, modelIdPrefix)
  );
  const targetOnlyModels = new Map();
  for (const row of activeRows) {
    if (!declaredModels.has(row.upstreamModel)) {
      return { error: 'upstream_not_declared' };
    }
    if (!row.targetOnly) continue;

    const upstreamKey = normalizeModelKeyV820(row.upstreamModel);
    if (publicKeys.has(upstreamKey)) {
      return { error: 'hidden_target_is_public_name' };
    }
    targetOnlyModels.set(upstreamKey, row.upstreamModel);
  }

  const mapping = Object.create(null);
  activeRows.forEach((row) => {
    mapping[row.publicModel] = row.upstreamModel;
  });

  return {
    error: '',
    modelMapping: activeRows.length > 0 ? JSON.stringify(mapping, null, 2) : '',
    targetOnlyModels: Array.from(targetOnlyModels.values()).sort(
      (left, right) => left.localeCompare(right)
    ),
  };
};
